/**
 * marcusCursorQueue — serialises cursor moves so a burst of tool calls
 * from Marcus animates sequentially instead of all cancelling each other.
 *
 * Ported from sagaActionRuntime.ts cursor queue (lines 31-142) with
 * marcus-cursor-* event names.
 */
import { appEvents, type MarcusCursorSource } from "@/shared/lib/events";

export interface CursorMoveDetail {
  target: string;
  comment?: string;
  pulses?: number;
  durationMs?: number;
  source?: MarcusCursorSource;
  showTooltip?: boolean;
  /**
   * Bias where the ghost cursor lands inside the target's bounding rect:
   * - "top": near the top edge (good for tall containers — lands near the
   *   first list item rather than the random vertical center)
   * - "center": (default) random near visual center
   * - "bottom": near the bottom edge
   */
  landingPosition?: "top" | "center" | "bottom";
}

interface QueuedCursorMove extends CursorMoveDetail {
  stepId: string;
}

const CURSOR_STEP_BASE_MS = 900;
const CURSOR_STEP_JITTER_MS = 130;
const ONBOARDING_STEP_DELAY_MS = 5600;
const ONBOARDING_CURSOR_MS = 1200;
const ONBOARDING_PULSES = 5;
const ONBOARDING_GATE_TARGET = "marcus-onboarding-next-step";
const ONBOARDING_SPEECH_BASE_MS = 1100;
const ONBOARDING_SPEECH_WORD_MS = 320;
const ONBOARDING_SPEECH_MAX_MS = 14000;
const GUIDED_FLOW_SPEECH_MIN_MS = 1800;
const GUIDED_FLOW_SPEECH_MAX_MS = 22000;
const ONBOARDING_EXPLORE_GATE_COMMENT =
  "Whenever you're ready, click 'Next step' and we'll dive into Static Ads.";
const ONBOARDING_IMAGE_GATE_COMMENT =
  "Now that we've seen static formats, let's click 'Next step' to explore Video Ads.";
const ONBOARDING_FINAL_GATE_COMMENT =
  "With creatives covered, let's click 'Next step' to see how Mads turns winners into managed spend.";
const ONBOARDING_AGENT_LIST_GATE_COMMENT =
  "To make this all happen, let's click 'Next step' to meet the specialist agents.";
const ONBOARDING_STATIC_CLONE_GATE_COMMENT =
  "Now that you've met the team, let's click 'Next step' to try a quick static ad clone.";
const ONBOARDING_FINISH_GATE_COMMENT = "Just one last step wraps up the tour.";
const ONBOARDING_QUICK_STATIC_ADCLONE_COMMENT =
  "To make it concrete, let's try a quick static ad clone with your product.";
const ONBOARDING_STATIC_ADCLONE_ADD_PRODUCT_COMMENT =
  "You can add a new product to start discovering similar ads for it in Meta.";
const ONBOARDING_STATIC_ADCLONE_UPLOAD_IMAGE_COMMENT =
  "Simply add an image here — we'll extract the product details and run relevant searches for it.";
const ONBOARDING_STATIC_ADCLONE_AI_ADD_COMMENT =
  "Now click Add Product so Gemini can extract the catalog details from the image.";
const ONBOARDING_STATIC_ADCLONE_SEARCH_COMMENT =
  "Now click Search Again to start pulling proven Meta ads for this product.";
const ONBOARDING_STATIC_ADCLONE_PICK_AD_COMMENT =
  "Once the running ads show up, pick one and we'll start the cloning process from there.";
const ONBOARDING_GATE_COMMENTS = new Set([
  ONBOARDING_EXPLORE_GATE_COMMENT,
  ONBOARDING_IMAGE_GATE_COMMENT,
  ONBOARDING_FINAL_GATE_COMMENT,
  ONBOARDING_AGENT_LIST_GATE_COMMENT,
  ONBOARDING_STATIC_CLONE_GATE_COMMENT,
  ONBOARDING_FINISH_GATE_COMMENT,
]);
const ONBOARDING_ALLOWED_TARGET_COMMENTS = new Map<string, ReadonlySet<string>>([
  ["static-ads-clone", new Set([ONBOARDING_QUICK_STATIC_ADCLONE_COMMENT])],
]);
const MAX_QUEUE = 10;
const cursorQueue: QueuedCursorMove[] = [];
let cursorWorkerActive = false;
let cursorStepSeq = 0;
const queueListeners = new Set<(n: number) => void>();

/**
 * Canonical onboarding tooltip copy keyed by highlight target. The runtime
 * Onboarding-sourced moves reinforce the tour with these strings — even if a
 * tool call passes a different `comment`, the canonical copy wins on these
 * targets so replays read the same every time and stay focused on ecommerce
 * outcomes (CTR, CAC, ROAS, first-day revenue, scaled spend) rather than
 * feature descriptions.
 *
 * Mirror this map with `MARCUS_ONBOARDING_CHOREOGRAPHY` in
 * `core/src/domains/agent-runtime/tools/ads/orchestration.ts` — both are
 * sources of truth for different surfaces (tool payload vs. queue floor).
 */
const ONBOARDING_DEFAULTS: Record<
  string,
  Required<Pick<CursorMoveDetail, "comment" | "pulses" | "durationMs">> & {
    landingPosition?: CursorMoveDetail["landingPosition"];
  }
> = {
  "explore-card-image-ads": {
    comment:
      "First, sales-ready static ads lift your click-through rate and turn cold traffic into first-day revenue.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "explore-card-video-ads": {
    comment:
      "Next, scroll-stopping video ads lower acquisition cost and keep cold audiences engaged.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "explore-card-meta-campaigns": {
    comment:
      "Finally, you can run and scale paid campaigns efficiently using the expertise of our agents.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "sidebar-nav-static-ads": {
    comment: "Here in Static Ads, fast click-through-rate tests begin.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-ads-new-ad": {
    comment: "Spin up a sales-ready static ad without a designer or agency.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-ads-clone": {
    comment:
      "You can start by cloning proven static ads into brand-safe variants, so your testing begins with evidence.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-ads-trending": {
    comment:
      "Alternatively, you can use proven formats for hooks, offers, and comparisons to launch new CTR tests quickly.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-discover-tab": {
    comment:
      "Discover proven image ads for your product, then Marcus and Banan can turn one into your own test.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-product-picker-add": {
    comment: ONBOARDING_STATIC_ADCLONE_ADD_PRODUCT_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-product-image-upload": {
    comment: ONBOARDING_STATIC_ADCLONE_UPLOAD_IMAGE_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-product-ai-add": {
    comment: ONBOARDING_STATIC_ADCLONE_AI_ADD_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-discover-button": {
    comment: ONBOARDING_STATIC_ADCLONE_SEARCH_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "static-adclone-discovered-ads": {
    comment: ONBOARDING_STATIC_ADCLONE_PICK_AD_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "sidebar-nav-video-ads": {
    comment: "Over in Video Ads, you unlock a powerful lever to lower CAC when image fatigue hits.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "video-ads-new-ad": {
    comment: "Launch a UGC or template video without booking a creator or shooting on set.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "video-ads-clone": {
    comment:
      "First, our Adclone agents can find winning videos, reverse-engineer them, and adapt them for your brand.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "video-ads-ugc": {
    comment:
      "If you need original content, you can create tailored UGC clips fast around your product and audience.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "video-ads-trending": {
    comment:
      "Or, you can simply start from proven templates to produce polished video ads without the guesswork.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "sidebar-nav-meta-campaigns": {
    comment:
      "Moving on to Meta campaigns, this is where winning creatives turn into scaling spend.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "new-meta-campaign": {
    comment:
      "Launch a structured Meta campaign — targeting, budget, and creatives wired for measurable ROAS.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "meta-agent-tasks-tab": {
    comment:
      "Here, Agent Tasks make campaign work concrete. Pick a job, review the brief, and let Agent Mads handle the launch and optimization.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "ads-agent-side-panel": {
    comment:
      "Use the side panel on the right to access me for tasks and communicate directly with the agents I spawn while we work on each of these features.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-list": {
    comment:
      "This side panel is your agent team. They collaborate with you to execute the features we just saw.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
    // The chat list panel is tall — bias the cursor toward the top so the
    // first beat lands near Marcus rather than randomly inside the panel
    // (which historically landed near Mads).
    landingPosition: "top",
  },
  "companion-agent-list-team-sweep": {
    comment:
      "Each agent spawns here as you start a related task. Specialist chats stay hidden until that task activates them, then you can open the agent here to refine creatives or tune campaigns.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
    landingPosition: "center",
  },
  "companion-agent-row-marcus": {
    comment: "I stay at the top for strategy, orchestration, and answering any questions.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-row-banan": {
    comment:
      "Agent Banan generates static ad images in every format and builds moodboards for video — and can design and edit in any language.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-row-vid": {
    comment:
      "Agent Vid produces and edits high-converting UGC and other video formats with broadcast-quality results.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-row-scout": {
    comment:
      "Agent Scout researches X, TikTok, YouTube, product reviews, and the wider web to surface winning hooks, scripts, and business insights for your product.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-row-gads": {
    comment: "Agent G prepares Google Ads campaigns from your approved strategy and assets.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  "companion-agent-row-meta_ads": {
    comment:
      "And finally, Agent Mads runs your Meta campaign tasks from initial brief to ongoing optimization.",
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
  [ONBOARDING_GATE_TARGET]: {
    comment: ONBOARDING_EXPLORE_GATE_COMMENT,
    pulses: ONBOARDING_PULSES,
    durationMs: ONBOARDING_CURSOR_MS,
  },
};

function isOnboardingCursorMove(detail: CursorMoveDetail): boolean {
  return detail.source === "onboarding";
}

function withOnboardingDefaults(detail: CursorMoveDetail): CursorMoveDetail {
  if (!isOnboardingCursorMove(detail)) return detail;
  const defaults = ONBOARDING_DEFAULTS[detail.target];
  if (!defaults) return detail;
  const allowedTargetComments = ONBOARDING_ALLOWED_TARGET_COMMENTS.get(detail.target);
  const suppliedTargetComment =
    detail.comment && allowedTargetComments?.has(detail.comment) ? detail.comment : null;
  const landingPosition = defaults.landingPosition ?? detail.landingPosition;
  // Canonical narration wins over any LLM-supplied comment so replays read
  // the same every time. Pulses/duration also pin to the onboarding rhythm.
  return {
    ...detail,
    comment:
      detail.target === ONBOARDING_GATE_TARGET &&
      detail.comment &&
      ONBOARDING_GATE_COMMENTS.has(detail.comment)
        ? detail.comment
        : suppliedTargetComment
          ? suppliedTargetComment
          : defaults.comment,
    pulses: defaults.pulses,
    durationMs: defaults.durationMs,
    ...(landingPosition ? { landingPosition } : {}),
  };
}

export function estimateOnboardingSpeechDelayMs(comment: string | undefined): number {
  return estimateSpeechDelayMs(comment, ONBOARDING_STEP_DELAY_MS, ONBOARDING_SPEECH_MAX_MS);
}

function estimateSpeechDelayMs(comment: string | undefined, minMs: number, maxMs: number): number {
  const wordCount = comment?.trim().split(/\s+/).filter(Boolean).length ?? 0;
  if (wordCount === 0) return minMs;
  const estimated = ONBOARDING_SPEECH_BASE_MS + wordCount * ONBOARDING_SPEECH_WORD_MS;
  return Math.max(minMs, Math.min(maxMs, estimated));
}

// Fallback only — the real per-step cadence comes from
// `marcus-cursor-step-complete` events published by the onboarding overlay
// once narration finishes. The estimate just keeps the queue from
// deadlocking if those events never arrive.
function nextCursorStepDelay(detail: CursorMoveDetail): number {
  if (
    isOnboardingCursorMove(detail) &&
    ONBOARDING_DEFAULTS[detail.target] &&
    detail.target !== ONBOARDING_GATE_TARGET
  ) {
    return estimateOnboardingSpeechDelayMs(detail.comment);
  }
  if (detail.source === "guided-flow" && detail.comment) {
    return estimateSpeechDelayMs(
      detail.comment,
      GUIDED_FLOW_SPEECH_MIN_MS,
      GUIDED_FLOW_SPEECH_MAX_MS,
    );
  }
  const r = (Math.random() + Math.random()) / 2 - 0.5;
  return Math.max(620, Math.min(1180, CURSOR_STEP_BASE_MS + r * 2 * CURSOR_STEP_JITTER_MS));
}

export const ONBOARDING_CURSOR_STEP_DELAY_MS = ONBOARDING_STEP_DELAY_MS;

function nextCursorStepFallbackMs(detail: CursorMoveDetail): number {
  const moveMs =
    detail.durationMs ??
    (isOnboardingCursorMove(detail) && ONBOARDING_DEFAULTS[detail.target]
      ? ONBOARDING_CURSOR_MS
      : 0);
  return moveMs + nextCursorStepDelay(detail) + 250;
}

function createCursorStepId(): string {
  cursorStepSeq += 1;
  return `marcus-cursor-step-${cursorStepSeq}`;
}

function notifyQueueListeners(): void {
  for (const fn of queueListeners) fn(cursorQueue.length);
}

function waitForCursorStepComplete(
  stepId: string,
  target: string,
  fallbackMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      window.clearTimeout(fallbackTimer);
      resolve();
    };
    const unsubscribe = appEvents.subscribe("marcus-cursor-step-complete", (event) => {
      if (event.stepId !== stepId || event.target !== target) return;
      finish();
    });
    const fallbackTimer = window.setTimeout(finish, fallbackMs);
  });
}

async function drainCursorQueue(): Promise<void> {
  if (cursorWorkerActive) return;
  cursorWorkerActive = true;
  try {
    while (cursorQueue.length > 0) {
      const queued = cursorQueue.shift()!;
      const detail = withOnboardingDefaults(queued);
      const stepId = queued.stepId;
      notifyQueueListeners();
      appEvents.publish({ type: "marcus-cursor-move-to", stepId, ...detail });
      await waitForCursorStepComplete(stepId, detail.target, nextCursorStepFallbackMs(detail));
    }
  } finally {
    cursorWorkerActive = false;
    notifyQueueListeners();
  }
}

export function enqueueCursorMove(detail: CursorMoveDetail): string {
  const stepId = createCursorStepId();
  if (cursorQueue.length >= MAX_QUEUE) {
    cursorQueue.shift();
  }
  cursorQueue.push({ ...detail, stepId });
  notifyQueueListeners();
  void drainCursorQueue();
  return stepId;
}

/**
 * Enqueue a cursor move and resolve once `marcus-cursor-arrived` fires
 * for the matching target. Bounded by timeoutMs so a missing element
 * doesn't deadlock the caller.
 */
export function moveCursorAndWait(detail: CursorMoveDetail, timeoutMs = 1500): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      window.clearTimeout(fallbackTimer);
      resolve();
    };
    const unsubscribe = appEvents.subscribe("marcus-cursor-arrived", (event) => {
      if (event.target && event.target !== detail.target) return;
      if (detail.source && event.source && event.source !== detail.source) return;
      finish();
    });
    const fallbackTimer = window.setTimeout(finish, timeoutMs);
    enqueueCursorMove(detail);
  });
}

export function subscribeToCursorQueue(fn: (n: number) => void): () => void {
  queueListeners.add(fn);
  fn(cursorQueue.length);
  return () => {
    queueListeners.delete(fn);
  };
}

export function getCursorQueueLength(): number {
  return cursorQueue.length;
}

// For testing — reset internal state
export function _resetQueue(): void {
  cursorQueue.length = 0;
  cursorWorkerActive = false;
  cursorStepSeq = 0;
  queueListeners.clear();
}
